<template>
  <div class="consent-promotion">
    <a href="javascript:void(0)" class="logo" @click="onClickHome">
      <img :src="require('@/assets/images/common/ci-wavve.svg')" alt="Wavve" />
    </a>
    <div class="consent-promotion-box">
      <h1 class="title" v-html="title"></h1>
      <p class="description" v-html="description"></p>
      <div class="table">
        <strong class="table-title">{{ tableTitle }}</strong>
        <div class="table-content">
          <dl v-for="item in tableContent" :key="item?.title">
            <dt>{{ item.title }}</dt>
            <dd>{{ item.content }}</dd>
          </dl>
        </div>
      </div>
      <ul class="notice">
        <li v-for="notice in noticeList" :key="notice">{{ notice }}</li>
      </ul>
      <div class="button-area">
        <div class="button-wrapper">
          <button
            class="button button-gray"
            type="button"
            @click="onClickDisagree(id)"
          >
            가격 변경 미동의
          </button>
          <button
            class="button button-primary"
            type="button"
            @click="onClickAgree(id)"
          >
            가격 변경 동의
          </button>
        </div>
        <button
          v-if="!isPage"
          class="button-popup-close"
          @click="onClickPopupHidden"
        >
          7일동안 팝업 보지 않기
        </button>
      </div>
    </div>
    <PpvToast />
  </div>
</template>
<script setup>
  import { ref, defineProps, computed, onUnmounted } from 'vue'
  import { setNumberCommas } from '@/common/utils/common'
  import { useConsentRenew } from '@/common/use/useConsentRenew'
  import { useRoute } from 'vue-router/composables'
  import { useRouterComposable } from '@/common/use/useRouterComposable'
  import PpvToast from '@/components/movie/PpvToast.vue'
  import {
    CONSENT_PROMOTION_DESCRIPTION,
    CONSENT_PROMOTION_NOTICE_LIST,
    CONSENT_PROMOTION_TITLE,
    ROUTER_PATH_CONSENT_PROMOTION,
  } from '@/common/utils/consent/const'
  import { getTextBreaks } from '@/common/utils/string-manipulation/get-text-breaks'

  const route = useRoute()
  const { pushRouter } = useRouterComposable()

  const props = defineProps({
    promotionInfo: {
      type: Object,
      default: () => ({}),
    },
  })

  const {
    ui,
    handleRenewAgree,
    handleRenewDisagree,
    handleConsentPopupHidden,
  } = useConsentRenew()

  const timer = ref(null)
  const timerMS = ref(2000)

  const id = computed(() => props.promotionInfo?.id ?? '')
  const currentPrice = computed(
    () => setNumberCommas(props.promotionInfo?.price) ?? '0',
  )
  const newPrice = computed(
    () => setNumberCommas(props.promotionInfo?.newPrice) ?? '0',
  )
  const startDate = computed(() =>
    (props.promotionInfo?.startDate ?? 'YYYY.MM.DD')?.replaceAll('-', '.'),
  )
  const dueDate = computed(() =>
    (props.promotionInfo?.dueDate ?? 'YYYY.MM.DD')?.replaceAll('-', '.'),
  )
  const isPage = computed(() => route.path === ROUTER_PATH_CONSENT_PROMOTION)

  // text
  const title = computed(() => getTextBreaks(CONSENT_PROMOTION_TITLE) ?? '')
  const description = computed(() => {
    return getTextBreaks(CONSENT_PROMOTION_DESCRIPTION)
      ?.replace('__START_DATE__', `${startDate.value}`)
      ?.replace('__DUE_DATE__', `${dueDate.value}`)
  })
  const noticeList = computed(
    () =>
      CONSENT_PROMOTION_NOTICE_LIST?.map((notice) => getTextBreaks(notice)) ??
      [],
  )

  // promotion text
  const tableTitle = ref('프로모션 가격 변경 안내')
  const tableContent = computed(() => [
    {
      title: '프로모션 가격',
      content: `${currentPrice.value}원`,
    },
    {
      title: '변경가격',
      content: `${newPrice.value}원`,
    },
  ])

  const onClickHome = () => {
    return pushRouter({ path: '/' })
  }

  const onClickPopupHidden = () => {
    return handleConsentPopupHidden()
  }

  const delayAction = (callback) => {
    timer.value = setTimeout(() => {
      callback()
    }, timerMS.value)
  }

  const succeed = () => {
    delayAction(() => {
      isPage.value ? onClickHome() : ui.closeLayerPopup()
    })
  }

  const failed = (isClose) => {
    if (isClose) {
      delayAction(() => {
        isPage.value ? onClickHome() : ui.closeLayerPopup()
      })
    }
  }

  const onClickDisagree = async (userId) => {
    await handleRenewDisagree({
      onSuccess: () => succeed(),
      onError: (isClose) => failed(isClose),
      userId,
    })
  }

  const onClickAgree = async (userId) => {
    await handleRenewAgree({
      onSuccess: () => succeed(),
      onError: (isClose) => failed(isClose),
      userId,
    })
  }

  onUnmounted(() => {
    clearTimeout(timer.value)
  })
</script>
<style scoped lang="scss">
  .consent-promotion {
    margin: 0 auto;
    padding: 23px 0;
    width: 1240px;
    font-weight: 400;
    &-box {
      width: 480px;
      margin: 147px auto 0;
      .title {
        font-size: 36px;
        line-height: 1.44;
        text-align: center;
        font-weight: 400;
        color: #fff;
      }
      .description {
        margin-top: 32px;
        font-size: 16px;
        line-height: 1.5;
        color: #a5a5a5;
        font-weight: 400;
      }
      .table {
        margin-top: 40px;
        border: 1px solid #2f2f2f;
        border-radius: 6px;
        &-title {
          display: block;
          padding: 12px 0;
          font-style: normal;
          font-weight: 400;
          text-align: center;
          font-size: 14px;
          line-height: 1.42;
          color: #a5a5a5;
          background-color: #2f2f2f;
        }
        &-content {
          display: flex;
          padding: 12px 20px;
          dl {
            width: 100%;
            text-align: center;
            dt {
              font-size: 14px;
              color: #7e7e7e;
            }
            dd {
              margin-top: 4px;
              font-size: 16px;
              color: #fff;
              font-weight: 400;
            }
            & + dl {
              position: relative;
              &::before {
                content: '';
                position: absolute;
                top: calc(50% - 10px);
                left: -10px;
                width: 20px;
                height: 20px;
                background: url('~@/assets/images/common/arrow-gray-tail.svg')
                  no-repeat center / cover;
              }
            }
          }
        }
      }
      .notice {
        margin-top: 20px;
        color: #7e7e7e;
        font-size: 14px;
        > li {
          position: relative;
          padding-left: 12px;
          &:before {
            content: '';
            position: absolute;
            top: 8px;
            left: 0;
            width: 4px;
            height: 4px;
            background-color: #7e7e7e;
            border-radius: 50%;
          }
        }
      }
      .button-area {
        .button-wrapper {
          margin-top: 40px;
          display: flex;
          .button {
            display: block;
            width: 100%;
            padding: 17px 0;
            border-radius: 30px;
            color: #fff;
            background-color: #0050ff;
            font-size: 18px;
            font-weight: 400;
            line-height: 1.44;
            & + .button {
              margin-left: 8px;
            }
            &.button-primary {
              background-color: #0050ff;
            }
            &.button-gray {
              background-color: #5e5e5e;
            }
          }
        }
        .button-popup-close {
          display: block;
          margin: 28px auto 0;
          font-size: 16px;
          line-height: 1.5;
          font-weight: 400;
          color: #9e9e9e;
        }
      }
    }
  }
</style>
